import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Car as CarIcon, ClipboardList, Database, LogOut, Plus, Pencil, Trash2,
  X, Upload, Download, Lock, Package, RefreshCw, Eye,
  CalendarDays as CalIcon, Settings as SettingsIcon, ChevronLeft, ChevronRight, Save, Mail, Inbox,
  KeyRound as KeyIcon, Star, ImagePlus, LayoutGrid, Facebook, Copy, Check, Scissors, MapPin,
} from "lucide-react";
import { api, mediaUrl, formatApiError, API } from "../../lib/api";
import Logo from "../../components/Logo";

const CATS = [
  { slug: "osobni", name: "Osobní vozy" },
  { slug: "dodavky", name: "Dodávky" },
  { slug: "luxusni", name: "Luxusní vozy" },
  { slug: "suv", name: "SUV / Terénní" },
  { slug: "sportovni", name: "Sportovní vozy" },
  { slug: "obytne", name: "Obytné vozy" },
  { slug: "voziky", name: "Vozíky a přívěsy" },
  { slug: "elektro", name: "Elektro vozy" },
];
const FUELS = ["Benzín", "Nafta", "Elektro", "Hybrid"];
const GEARS = ["Manuál", "Automat"];
const toSlug = (t) =>
  (t || "")
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();
const STATUSES = {
  nova: { label: "Nová", cls: "bg-blue-500/15 text-blue-400" },
  potvrzena: { label: "Potvrzená", cls: "bg-green-500/15 text-green-400" },
  dokoncena: { label: "Dokončená", cls: "bg-[#B58A50]/15 text-[#B58A50]" },
  zrusena: { label: "Zrušená", cls: "bg-red-500/15 text-red-400" },
};

const inputCls = "w-full bg-[#151515] border border-[#1f1f1f] text-white rounded-sm px-3 py-2.5 text-sm focus:border-[#B58A50] outline-none";
const labelCls = "text-white/50 text-xs uppercase tracking-wider";

const emptyCar = {
  name: "", slug: "", category: "osobni", engine: "", fuel: "Benzín", transmission: "Automat",
  seats: 5, price_per_day: 900, deposit: 10000, power_kw: 0, year: 0, mileage_limit: 0, description: "", image_url: "", images: [], available: true,
  available_from: "", available_to: "", featured: false, price_tiers: [],
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("vozy");

  useEffect(() => {
    if (localStorage.getItem("kame_role") !== "admin" || !localStorage.getItem("kame_token")) {
      navigate("/admin");
    }
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("kame_token");
    localStorage.removeItem("kame_role");
    localStorage.removeItem("kame_system_token");
    navigate("/admin");
  };

  const tabs = [
    { id: "vozy", label: "Vozy", icon: CarIcon },
    { id: "kategorie", label: "Kategorie", icon: LayoutGrid },
    { id: "rezervace", label: "Rezervace", icon: ClipboardList },
    { id: "kalendar", label: "Kalendář", icon: CalIcon },
    { id: "nastaveni", label: "Nastavení", icon: SettingsIcon },
    { id: "export", label: "Systém & Export", icon: Database },
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      <header className="border-b border-[#1a1a1a] bg-[#0a0a0a] sticky top-0 z-40">
        <div className="container-kame flex items-center justify-between h-[70px]">
          <Logo compact />
          <button onClick={logout} className="btn-outline-gold text-xs py-2 px-4" data-testid="admin-logout">
            <LogOut size={16} /> Odhlásit
          </button>
        </div>
      </header>

      <div className="container-kame py-8">
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#1a1a1a]">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-wider font-medium border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-[#B58A50] text-[#B58A50]" : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              <t.icon size={17} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "vozy" && <CarsTab />}
        {tab === "kategorie" && <CategoriesTab />}
        {tab === "rezervace" && <ReservationsTab />}
        {tab === "kalendar" && <CalendarTab />}
        {tab === "nastaveni" && <SettingsTab />}
        {tab === "export" && <ExportTab />}
      </div>
    </div>
  );
}

/* ---------------- CARS ---------------- */
function CarsTab() {
  const [cars, setCars] = useState([]);
  const [editing, setEditing] = useState(null); // car object or null
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/cars").then((r) => setCars(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = async (id) => {
    if (!window.confirm("Opravdu smazat tento vůz?")) return;
    await api.delete(`/admin/cars/${id}`);
    toast.success("Vůz smazán");
    load();
  };

  return (
    <div data-testid="cars-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl text-white uppercase">Vozový park</h2>
          <p className="text-white/50 text-sm">{cars.length} vozidel celkem</p>
        </div>
        <button onClick={() => setEditing(emptyCar)} className="btn-gold text-sm py-2.5" data-testid="add-car-btn">
          <Plus size={18} /> Přidat vůz
        </button>
      </div>

      {loading ? (
        <p className="text-white/50">Načítám…</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cars.map((c) => (
            <div key={c.id} className="card-kame overflow-hidden" data-testid={`admin-car-${c.id}`}>
              <img src={mediaUrl(c.image_url)} alt={c.name} className="w-full h-36 object-cover bg-[#0a0a0a]" />
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <h3 className="font-display text-lg text-white uppercase">{c.name}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-sm ${c.available ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"}`}>
                    {c.available ? "Dostupné" : "Skryté"}
                  </span>
                </div>
                <p className="text-white/50 text-xs mt-1">{(CATS.find((x) => x.slug === c.category)?.name) || c.category} · {c.fuel} · {c.transmission}</p>
                <p className="font-display text-xl text-[#B58A50] mt-2">{c.price_per_day.toLocaleString("cs-CZ")} Kč <span className="text-xs text-white/40 font-sans">/ den</span></p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setEditing(c)} className="btn-outline-gold text-xs py-2 px-3 flex-1" data-testid={`edit-car-${c.id}`}>
                    <Pencil size={14} /> Upravit
                  </button>
                  <button onClick={() => del(c.id)} className="border border-red-500/40 text-red-400 rounded-sm px-3 py-2 hover:bg-red-500/10 transition-colors" data-testid={`delete-car-${c.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <CarForm car={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CarForm({ car, onClose, onSaved }) {
  const [form, setForm] = useState({ ...emptyCar, ...car });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [catOptions, setCatOptions] = useState(CATS);
  const fileRef = useRef();
  const isEdit = !!car.id;

  useEffect(() => {
    api.get("/categories")
      .then((r) => setCatOptions(r.data.map((c) => ({ slug: c.slug, name: c.name }))))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => {
          const images = [...(f.images || []), data.url];
          return { ...f, images, image_url: f.image_url || data.url };
        });
      }
      toast.success(list.length > 1 ? "Fotky nahrány" : "Fotka nahrána");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setMain = (url) => set("image_url", url);
  const removeImage = (url) => setForm((f) => {
    const images = (f.images || []).filter((x) => x !== url);
    const image_url = f.image_url === url ? (images[0] || "") : f.image_url;
    return { ...f, images, image_url };
  });
  const moveImage = (idx, dir) => setForm((f) => {
    const imgs = [...(f.images || [])];
    const j = idx + dir;
    if (j < 0 || j >= imgs.length) return f;
    [imgs[idx], imgs[j]] = [imgs[j], imgs[idx]];
    return { ...f, images: imgs };
  });
  const addTier = () => set("price_tiers", [...(form.price_tiers || []), { min_days: "", price_per_day: "" }]);
  const updateTier = (i, k, v) => set("price_tiers", (form.price_tiers || []).map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const removeTier = (i) => set("price_tiers", (form.price_tiers || []).filter((_, idx) => idx !== i));

  const gallery = (form.images && form.images.length) ? form.images : (form.image_url ? [form.image_url] : []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Zadejte název vozu.");
    setSaving(true);
    const payload = {
      ...form,
      seats: Number(form.seats),
      price_per_day: Number(form.price_per_day),
      deposit: Number(form.deposit) || 0,
      power_kw: Number(form.power_kw) || 0,
      year: Number(form.year) || 0,
      mileage_limit: Number(form.mileage_limit) || 0,
      available_from: form.available_from || null,
      available_to: form.available_to || null,
      images: form.images || [],
      price_tiers: (form.price_tiers || [])
        .filter((t) => t.min_days && t.price_per_day)
        .map((t) => ({ min_days: Number(t.min_days), price_per_day: Number(t.price_per_day) }))
        .sort((a, b) => a.min_days - b.min_days),
    };
    try {
      if (isEdit) await api.put(`/admin/cars/${car.id}`, payload);
      else await api.post("/admin/cars", payload);
      toast.success(isEdit ? "Vůz upraven" : "Vůz přidán");
      onSaved();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4" data-testid="car-form-modal">
      <div className="card-kame w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-[#1a1a1a]">
          <h3 className="font-display text-xl text-white uppercase">{isEdit ? "Upravit vůz" : "Přidat vůz"}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="car-form-close"><X size={22} /></button>
        </div>

        <form onSubmit={save} className="p-5 space-y-4">
          <div data-testid="car-gallery-manager">
            <label className={labelCls}>Fotky vozu</label>
            <div className="flex flex-wrap gap-3 mt-2">
              {gallery.map((url, i) => {
                const isMain = url === form.image_url;
                return (
                  <div key={i} className={`relative w-28 h-20 rounded-sm overflow-hidden border ${isMain ? "border-[#B58A50]" : "border-[#1f1f1f]"} group`} data-testid={`car-image-${i}`}>
                    <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" />
                    <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded-sm">{i + 1}.</span>
                    {isMain && <span className="absolute top-1 left-1 bg-[#B58A50] text-black text-[9px] px-1.5 py-0.5 rounded-sm font-semibold uppercase">Hlavní</span>}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                      <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} title="Posunout vlevo" className="text-white hover:text-[#B58A50] disabled:opacity-30" data-testid={`move-left-${i}`}><ChevronLeft size={17} /></button>
                      {!isMain && (
                        <button type="button" onClick={() => setMain(url)} title="Nastavit jako hlavní" className="text-white hover:text-[#B58A50]" data-testid={`set-main-${i}`}><Star size={16} /></button>
                      )}
                      <button type="button" onClick={() => removeImage(url)} title="Odebrat" className="text-white hover:text-red-400" data-testid={`remove-image-${i}`}><Trash2 size={16} /></button>
                      <button type="button" onClick={() => moveImage(i, 1)} disabled={i === gallery.length - 1} title="Posunout vpravo" className="text-white hover:text-[#B58A50] disabled:opacity-30" data-testid={`move-right-${i}`}><ChevronRight size={17} /></button>
                    </div>
                  </div>
                );
              })}
              <button type="button" onClick={() => fileRef.current.click()} disabled={uploading} className="w-28 h-20 rounded-sm border border-dashed border-[#333] hover:border-[#B58A50] text-white/50 hover:text-[#B58A50] flex flex-col items-center justify-center gap-1 transition-colors" data-testid="car-upload-btn">
                <ImagePlus size={20} />
                <span className="text-[10px]">{uploading ? "Nahrávám…" : "Přidat fotky"}</span>
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="car-image-input" />
            <p className="text-white/40 text-xs mt-2">Můžete nahrát více fotek. Najetím myší: ⭐ nastaví hlavní (náhledovou) fotku, šipky ← → mění pořadí, koš odebere fotku.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Název</label><input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls + " mt-1.5"} data-testid="car-name" /></div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Odkaz na vůz (URL)</label>
              <div className="flex gap-2 mt-1.5">
                <input value={form.slug || ""} onChange={(e) => set("slug", toSlug(e.target.value))} placeholder={toSlug(form.name) || "napr-volkswagen-passat"} className={inputCls} data-testid="car-slug" />
                <button type="button" onClick={() => set("slug", toSlug(form.name))} className="btn-outline-gold text-xs px-3 whitespace-nowrap" data-testid="car-slug-generate">Z názvu</button>
              </div>
              <p className="text-white/40 text-xs mt-1">
                Náhled: <span className="text-[#B58A50]">{(typeof window !== "undefined" ? window.location.origin : "")}/vuz/{form.slug || toSlug(form.name) || "..."}</span>
                <br />Necháte-li prázdné, odkaz se vytvoří automaticky z názvu.
              </p>
            </div>
            <div><label className={labelCls}>Kategorie</label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls + " mt-1.5"} data-testid="car-category">
                {catOptions.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Motorizace</label><input value={form.engine} onChange={(e) => set("engine", e.target.value)} placeholder="2.0 TDI" className={inputCls + " mt-1.5"} data-testid="car-engine" /></div>
            <div><label className={labelCls}>Cena za den (Kč)</label><input type="number" value={form.price_per_day} onChange={(e) => set("price_per_day", e.target.value)} className={inputCls + " mt-1.5"} data-testid="car-price" /></div>
            <div><label className={labelCls}>Vratná kauce (Kč)</label><input type="number" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} className={inputCls + " mt-1.5"} data-testid="car-deposit" /></div>
            <div><label className={labelCls}>Palivo</label>
              <select value={form.fuel} onChange={(e) => set("fuel", e.target.value)} className={inputCls + " mt-1.5"} data-testid="car-fuel">
                <option value="">— nezadáno —</option>
                {FUELS.map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Převodovka</label>
              <select value={form.transmission} onChange={(e) => set("transmission", e.target.value)} className={inputCls + " mt-1.5"} data-testid="car-transmission">
                <option value="">— nezadáno —</option>
                {GEARS.map((g) => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Počet míst</label><input type="number" value={form.seats} onChange={(e) => set("seats", e.target.value)} className={inputCls + " mt-1.5"} data-testid="car-seats" /></div>
            <div>
              <label className={labelCls}>Výkon (kW)</label>
              <input type="number" value={form.power_kw} onChange={(e) => set("power_kw", e.target.value)} className={inputCls + " mt-1.5"} data-testid="car-power" />
              <p className="text-[#B58A50] text-xs mt-1">{Number(form.power_kw) > 0 ? `≈ ${Math.round(Number(form.power_kw) * 1.35962)} koní` : "přepočet na koně automaticky"}</p>
            </div>
            <div><label className={labelCls}>Rok výroby</label><input type="number" value={form.year} onChange={(e) => set("year", e.target.value)} placeholder="2022" className={inputCls + " mt-1.5"} data-testid="car-year" /></div>
            <div><label className={labelCls}>Max. nájezd (km/den)</label><input type="number" value={form.mileage_limit} onChange={(e) => set("mileage_limit", e.target.value)} placeholder="např. 300" className={inputCls + " mt-1.5"} data-testid="car-mileage" /><p className="text-white/40 text-xs mt-1">0 = obecný text. Jinak se u vozu ukáže konkrétní nájezd km/den.</p></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelCls}>Nedostupné od</label><input type="date" value={form.available_from || ""} onChange={(e) => set("available_from", e.target.value)} className={inputCls + " mt-1.5"} data-testid="car-from" /></div>
              <div><label className={labelCls}>Nedostupné do</label><input type="date" value={form.available_to || ""} onChange={(e) => set("available_to", e.target.value)} className={inputCls + " mt-1.5"} data-testid="car-to" /></div>
            </div>
          </div>

          <p className="text-white/40 text-xs -mt-1 bg-[#0d0d0d] border border-[#1f1f1f] rounded-sm p-3" data-testid="availability-hint">
            <span className="text-[#B58A50]">Dostupnost:</span> Prázdná pole = vůz je <b>dostupný</b>. Vyplňte jen <b>„Nedostupné do"</b> = vůz je obsazený do daného data (pak zase dostupný — datum můžete každý měsíc posouvat). Vyplňte <b>od–do</b> = nedostupný v tom termínu. Jen <b>„Nedostupné od"</b> = nedostupný trvale od data.
          </p>

          <div><label className={labelCls}>Popis</label><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inputCls + " mt-1.5 resize-none"} data-testid="car-description" /></div>

          <div data-testid="car-tiers-editor">
            <div className="flex items-center justify-between">
              <label className={labelCls}>Ceny za delší pronájem</label>
              <button type="button" onClick={addTier} className="text-[#B58A50] text-xs uppercase tracking-wider hover:underline flex items-center gap-1" data-testid="add-tier">
                <Plus size={14} /> Přidat hladinu
              </button>
            </div>
            <p className="text-white/40 text-xs mt-1">Např. od 10 dní 850 Kč/den, od 14 dní 790 Kč/den. Zvýhodněná sazba se v rezervaci uplatní automaticky.</p>
            <div className="space-y-2 mt-3">
              {(form.price_tiers || []).length === 0 && (
                <p className="text-white/30 text-xs">Zatím žádné zvýhodněné sazby.</p>
              )}
              {(form.price_tiers || []).map((t, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`tier-row-${i}`}>
                  <span className="text-white/50 text-sm">od</span>
                  <input type="number" min="2" value={t.min_days} onChange={(e) => updateTier(i, "min_days", e.target.value)} placeholder="10" className={inputCls + " w-20"} data-testid={`tier-days-${i}`} />
                  <span className="text-white/50 text-sm">dní</span>
                  <span className="text-white/30">→</span>
                  <input type="number" value={t.price_per_day} onChange={(e) => updateTier(i, "price_per_day", e.target.value)} placeholder="850" className={inputCls + " w-28"} data-testid={`tier-price-${i}`} />
                  <span className="text-white/50 text-sm">Kč/den</span>
                  <button type="button" onClick={() => removeTier(i)} className="text-red-400 hover:text-red-300 ml-1" data-testid={`remove-tier-${i}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
              <input type="checkbox" checked={form.available} onChange={(e) => set("available", e.target.checked)} className="accent-[#B58A50] w-4 h-4" data-testid="car-available" /> Zobrazit na webu
            </label>
            <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#B58A50] w-4 h-4" data-testid="car-featured" /> Doporučený
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold flex-1 justify-center">Zrušit</button>
            <button type="submit" disabled={saving} className="btn-gold flex-1 justify-center disabled:opacity-60" data-testid="car-save">
              {saving ? "Ukládám…" : "Uložit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- RESERVATIONS ---------------- */
function ReservationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/reservations").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setStatus = async (id, status) => {
    await api.put(`/admin/reservations/${id}/status`, { status });
    toast.success("Stav aktualizován");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Smazat rezervaci?")) return;
    await api.delete(`/admin/reservations/${id}`);
    toast.success("Rezervace smazána");
    setOpen(null);
    load();
  };

  return (
    <div data-testid="reservations-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl text-white uppercase">Rezervace</h2>
          <p className="text-white/50 text-sm">{items.length} rezervací</p>
        </div>
        <button onClick={load} className="btn-outline-gold text-xs py-2 px-4"><RefreshCw size={15} /> Obnovit</button>
      </div>

      {loading ? (
        <p className="text-white/50">Načítám…</p>
      ) : items.length === 0 ? (
        <div className="card-kame p-10 text-center text-white/60" data-testid="reservations-empty">Zatím žádné rezervace.</div>
      ) : (
        <div className="card-kame overflow-hidden overflow-x-auto">
          <table className="w-full text-left text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-[#1f1f1f] text-white/50 text-xs uppercase tracking-wider">
                <th className="py-3 px-4">Zákazník</th>
                <th className="py-3 px-4">Vůz</th>
                <th className="py-3 px-4">Termín</th>
                <th className="py-3 px-4">Cena</th>
                <th className="py-3 px-4">Stav</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-[#141414] hover:bg-[#0d0d0d]" data-testid={`reservation-row-${r.id}`}>
                  <td className="py-3 px-4 text-white">{r.customer_name}<br /><span className="text-white/40 text-xs">{r.phone}</span></td>
                  <td className="py-3 px-4 text-white/80">{r.car_name}</td>
                  <td className="py-3 px-4 text-white/60 text-xs">{r.pickup_date} → {r.return_date}<br />{r.days} dní</td>
                  <td className="py-3 px-4 font-display text-[#B58A50]">{(r.total_price || 0).toLocaleString("cs-CZ")} Kč</td>
                  <td className="py-3 px-4">
                    <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className={`text-xs rounded-sm px-2 py-1 bg-[#151515] border border-[#1f1f1f] ${STATUSES[r.status]?.cls || ""}`} data-testid={`reservation-status-${r.id}`}>
                      {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <button onClick={() => setOpen(r)} className="text-[#B58A50] hover:underline mr-3" data-testid={`reservation-view-${r.id}`}><Eye size={16} /></button>
                    <button onClick={() => del(r.id)} className="text-red-400 hover:underline"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="card-kame w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="reservation-detail">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl text-white uppercase">Detail rezervace</h3>
              <button onClick={() => setOpen(null)} className="text-white/50 hover:text-white"><X size={22} /></button>
            </div>
            <div className="space-y-2 text-sm">
              <D l="Zákazník" v={open.customer_name} />
              <D l="Telefon" v={open.phone} />
              <D l="E-mail" v={open.email} />
              <D l="Adresa" v={open.address || "—"} />
              <D l="Vůz" v={open.car_name} />
              <D l="Vyzvednutí" v={`${open.pickup_date} v ${open.pickup_time}`} />
              <D l="Vrácení" v={`${open.return_date} v ${open.return_time}`} />
              <D l="Doba" v={`${open.days} dní`} />
              <D l="Doplňky" v={open.extras?.length ? open.extras.join(", ") : "—"} />
              <D l="Platba" v={open.payment_method === "online" ? "Bankovní převod" : "Při převzetí"} />
              <D l="Poznámka" v={open.note || "—"} />
              <D l="Celkem" v={`${(open.total_price || 0).toLocaleString("cs-CZ")} Kč`} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const D = ({ l, v }) => (
  <div className="flex justify-between gap-4 border-b border-[#141414] py-1.5">
    <span className="text-white/50">{l}</span>
    <span className="text-white text-right">{v}</span>
  </div>
);

/* ---------------- CATEGORIES ---------------- */
function CategoriesTab() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSlug, setSavingSlug] = useState("");
  const [uploadingSlug, setUploadingSlug] = useState("");
  const [newCat, setNewCat] = useState(null);
  const [creating, setCreating] = useState(false);
  const fileRefs = useRef({});
  const newFileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get("/admin/categories").then((r) => setCats(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setField = (slug, k, v) => setCats((list) => list.map((c) => (c.slug === slug ? { ...c, [k]: v } : c)));

  const upload = async (slug, file) => {
    if (!file) return;
    setUploadingSlug(slug);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField(slug, "image_url", data.url);
      toast.success("Fotka nahrána — nezapomeňte uložit");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploadingSlug("");
      if (fileRefs.current[slug]) fileRefs.current[slug].value = "";
    }
  };

  const save = async (c) => {
    setSavingSlug(c.slug);
    try {
      await api.put(`/admin/categories/${c.slug}`, { name: c.name, description: c.description, image_url: c.image_url });
      toast.success("Kategorie uložena");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingSlug("");
    }
  };

  const uploadNew = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewCat((n) => ({ ...n, image_url: data.url }));
      toast.success("Fotka nahrána");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      if (newFileRef.current) newFileRef.current.value = "";
    }
  };

  const createCat = async () => {
    if (!newCat.name.trim()) return toast.error("Zadejte název kategorie.");
    setCreating(true);
    try {
      await api.post("/admin/categories", newCat);
      toast.success("Kategorie přidána");
      setNewCat(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const deleteCat = async (slug) => {
    if (!window.confirm("Opravdu smazat tuto kategorii?")) return;
    try {
      await api.delete(`/admin/categories/${slug}`);
      toast.success("Kategorie smazána");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <p className="text-white/50">Načítám…</p>;

  return (
    <div data-testid="categories-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl text-white uppercase">Kategorie vozů</h2>
          <p className="text-white/50 text-sm">Upravte fotku, název a popis, které se zobrazují na hlavní stránce.</p>
        </div>
        <button
          onClick={() => setNewCat({ name: "", description: "", image_url: "", price_from: 900 })}
          className="btn-gold text-sm py-2.5"
          data-testid="add-category-btn"
        >
          <Plus size={18} /> Přidat kategorii
        </button>
      </div>

      {newCat && (
        <div className="card-kame p-5 mb-5 border-[#B58A50]/40" data-testid="new-category-form">
          <h3 className="font-display text-lg text-white uppercase mb-4">Nová kategorie</h3>
          <div className="grid sm:grid-cols-[auto_1fr] gap-4">
            <div className="w-32 h-24 rounded-sm overflow-hidden bg-[#0a0a0a] border border-[#1f1f1f] flex items-center justify-center">
              {newCat.image_url ? <img src={mediaUrl(newCat.image_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-white/30 text-xs px-2 text-center">Bez fotky</span>}
            </div>
            <div className="space-y-3">
              <input ref={newFileRef} type="file" accept="image/*" hidden onChange={(e) => uploadNew(e.target.files[0])} data-testid="new-category-image-input" />
              <button type="button" onClick={() => newFileRef.current.click()} className="btn-outline-gold text-xs py-2 px-3" data-testid="new-category-upload"><ImagePlus size={14} /> Nahrát fotku</button>
              <input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} placeholder="Název kategorie" className={inputCls} data-testid="new-category-name" />
              <textarea value={newCat.description} onChange={(e) => setNewCat({ ...newCat, description: e.target.value })} rows={2} placeholder="Popis" className={inputCls + " resize-none"} data-testid="new-category-desc" />
              <div className="flex gap-3">
                <button onClick={() => setNewCat(null)} className="btn-outline-gold text-xs py-2 px-4">Zrušit</button>
                <button onClick={createCat} disabled={creating} className="btn-gold text-xs py-2 px-4 disabled:opacity-60" data-testid="new-category-save"><Save size={15} /> {creating ? "Ukládám…" : "Vytvořit"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {cats.map((c) => (
          <div key={c.slug} className="card-kame p-5" data-testid={`category-card-${c.slug}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-wider text-[#B58A50] bg-[#B58A50]/10 px-2 py-0.5 rounded-sm">{c.slug}{c.custom ? " · vlastní" : ""}</span>
              {c.custom && (
                <button onClick={() => deleteCat(c.slug)} className="text-red-400 hover:text-red-300" title="Smazat kategorii" data-testid={`category-delete-${c.slug}`}><Trash2 size={16} /></button>
              )}
            </div>
            <div className="flex gap-4">
              <div className="w-32 h-24 rounded-sm overflow-hidden bg-[#0a0a0a] border border-[#1f1f1f] shrink-0 flex items-center justify-center">
                {c.image_url ? (
                  <img src={mediaUrl(c.image_url)} alt={c.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white/30 text-xs text-center px-2">Výchozí obrázek</span>
                )}
              </div>
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <input ref={(el) => (fileRefs.current[c.slug] = el)} type="file" accept="image/*" hidden onChange={(e) => upload(c.slug, e.target.files[0])} data-testid={`category-image-input-${c.slug}`} />
                <button type="button" onClick={() => fileRefs.current[c.slug].click()} disabled={uploadingSlug === c.slug} className="btn-outline-gold text-xs py-2 px-3" data-testid={`category-upload-${c.slug}`}>
                  <ImagePlus size={14} /> {uploadingSlug === c.slug ? "Nahrávám…" : "Nahrát fotku"}
                </button>
                {c.image_url && (
                  <button type="button" onClick={() => setField(c.slug, "image_url", "")} className="text-red-400 text-xs mt-2 hover:underline text-left" data-testid={`category-clear-image-${c.slug}`}>
                    Odebrat fotku
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className={labelCls}>Název</label>
                <input value={c.name} onChange={(e) => setField(c.slug, "name", e.target.value)} className={inputCls + " mt-1.5"} data-testid={`category-name-${c.slug}`} />
              </div>
              <div>
                <label className={labelCls}>Popis</label>
                <textarea value={c.description} onChange={(e) => setField(c.slug, "description", e.target.value)} rows={2} className={inputCls + " mt-1.5 resize-none"} data-testid={`category-desc-${c.slug}`} />
              </div>
              <button onClick={() => save(c)} disabled={savingSlug === c.slug} className="btn-gold text-xs py-2.5 disabled:opacity-60" data-testid={`category-save-${c.slug}`}>
                <Save size={16} /> {savingSlug === c.slug ? "Ukládám…" : "Uložit kategorii"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- CALENDAR ---------------- */
const MONTHS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const DOW = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
const iso = (d) => {
  const x = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return x.toISOString().split("T")[0];
};
const STATUS_DOT = { nova: "bg-blue-400", potvrzena: "bg-green-400", dokoncena: "bg-[#B58A50]", zrusena: "bg-red-400", blokace: "bg-orange-500" };

function CalendarTab() {
  const [cars, setCars] = useState([]);
  const [res, setRes] = useState([]);
  const [carFilter, setCarFilter] = useState("all");
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [form, setForm] = useState(null);
  const [dayView, setDayView] = useState(null);
  const [split, setSplit] = useState(null);
  const [blockEdit, setBlockEdit] = useState(null);

  const load = () => api.get("/admin/reservations").then((r) => setRes(r.data)).catch(() => {});
  const loadCars = () => api.get("/admin/cars").then((r) => setCars(r.data)).catch(() => {});
  const refresh = () => { load(); loadCars(); };
  useEffect(() => { loadCars(); load(); }, []);

  const filtered = carFilter === "all" ? res : res.filter((r) => r.car_id === carFilter);
  const year = cursor.getFullYear(), month = cursor.getMonth();
  const startDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const filteredCars = carFilter === "all" ? cars : cars.filter((c) => c.id === carFilter);
  const carBlockedOn = (car, ds) => {
    const f = (car.available_from || "").trim();
    const t = (car.available_to || "").trim();
    if (!f && !t) return false;
    if (f && t) return f <= ds && ds <= t;
    if (f) return ds >= f;
    return ds <= t;
  };
  const dayItems = (date) => {
    const ds = iso(date);
    const reservations = filtered
      .filter((r) => r.pickup_date <= ds && ds < r.return_date)
      .map((r) => ({ type: "reservation", ...r }));
    const blocks = filteredCars
      .filter((c) => carBlockedOn(c, ds))
      .map((c) => ({ type: "block", car_id: c.id, car_name: c.name, customer_name: "Blokace vozidla", status: "blokace", car: c }));
    return [...reservations, ...blocks];
  };
  const move = (n) => setCursor(new Date(year, month + n, 1));
  const todayIso = iso(new Date());
  const dayViewItems = dayView ? dayItems(dayView) : [];

  const openNew = (dateIso) => setForm({ car_id: cars[0]?.id || "", pickup_date: dateIso || todayIso, return_date: "", customer_name: "", phone: "", email: "", note: "", status: "potvrzena" });
  const editReservation = (r) => setForm({ ...r });
  const delRes = async (id) => {
    if (!window.confirm("Opravdu smazat tuto rezervaci / blokaci?")) return;
    try { await api.delete(`/admin/reservations/${id}`); toast.success("Smazáno"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="calendar-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div>
          <h2 className="font-display text-2xl text-white uppercase">Kalendář rezervací</h2>
          <p className="text-white/50 text-sm">Klikněte na den pro přehled a úpravy termínů</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={carFilter} onChange={(e) => setCarFilter(e.target.value)} className={inputCls + " w-auto"} data-testid="calendar-car-filter">
            <option value="all">Všechna vozidla</option>
            {cars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={() => openNew()} className="btn-gold text-sm py-2.5" data-testid="add-manual-res">
            <Plus size={18} /> Přidat termín
          </button>
        </div>
      </div>

      <div className="card-kame p-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => move(-1)} className="text-white/60 hover:text-[#B58A50] p-2" data-testid="cal-prev"><ChevronLeft size={22} /></button>
          <p className="font-display text-xl text-white uppercase">{MONTHS[month]} {year}</p>
          <button onClick={() => move(1)} className="text-white/60 hover:text-[#B58A50] p-2" data-testid="cal-next"><ChevronRight size={22} /></button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {DOW.map((d) => <div key={d} className="text-center text-white/40 text-xs uppercase py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((date, i) => {
            if (!date) return <div key={i} className="min-h-[84px]" />;
            const items = dayItems(date);
            const isToday = iso(date) === todayIso;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setDayView(date)}
                className={`min-h-[84px] text-left rounded-sm border p-1.5 transition-colors hover:border-[#B58A50] hover:bg-[#151515] ${isToday ? "border-[#B58A50]" : "border-[#1a1a1a]"} bg-[#0d0d0d]`}
                data-testid={`cal-day-${iso(date)}`}
              >
                <div className="text-xs text-white/50 mb-1">{date.getDate()}</div>
                <div className="space-y-1">
                  {items.slice(0, 3).map((r, j) => (
                    <div key={j} className="flex items-center gap-1 text-[10px] text-white/80 truncate" title={`${r.car_name} — ${r.customer_name}`}>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[r.status] || "bg-white/40"}`} />
                      <span className="truncate">{r.car_name}</span>
                    </div>
                  ))}
                  {items.length > 3 && <div className="text-[10px] text-[#B58A50]">+{items.length - 3} další</div>}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#1a1a1a]">
          {Object.entries(STATUS_DOT).map(([k, cls]) => (
            <span key={k} className="flex items-center gap-2 text-xs text-white/50">
              <span className={`w-2.5 h-2.5 rounded-full ${cls}`} /> {STATUSES[k]?.label || "Blokace vozidla"}
            </span>
          ))}
        </div>
      </div>

      {dayView && (
        <DayDetailModal
          date={dayView}
          items={dayViewItems}
          onClose={() => setDayView(null)}
          onAdd={() => { const d = iso(dayView); setDayView(null); openNew(d); }}
          onEditRes={(r) => { setDayView(null); editReservation(r); }}
          onSplit={(r) => { setDayView(null); setSplit(r); }}
          onDeleteRes={delRes}
          onEditBlock={(car) => { setDayView(null); setBlockEdit(car); }}
        />
      )}

      {form && <ResForm form={form} setForm={setForm} cars={cars} onClose={() => setForm(null)} onSaved={() => { setForm(null); refresh(); }} />}
      {split && <SplitForm res={split} onClose={() => setSplit(null)} onSaved={() => { setSplit(null); refresh(); }} />}
      {blockEdit && <BlockForm car={blockEdit} onClose={() => setBlockEdit(null)} onSaved={() => { setBlockEdit(null); refresh(); }} />}
    </div>
  );
}

const fmtCz = (d) => { try { return new Date(d).toLocaleDateString("cs-CZ"); } catch { return d; } };

function DayDetailModal({ date, items, onClose, onAdd, onEditRes, onSplit, onDeleteRes, onEditBlock }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4" data-testid="day-detail-modal">
      <div className="card-kame w-full max-w-lg my-8">
        <div className="flex items-center justify-between p-5 border-b border-[#1a1a1a]">
          <div>
            <h3 className="font-display text-xl text-white uppercase">Přehled dne</h3>
            <p className="text-white/50 text-sm capitalize">{new Date(date).toLocaleDateString("cs-CZ", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="day-detail-close"><X size={22} /></button>
        </div>

        <div className="p-5 space-y-3">
          {items.length === 0 && <p className="text-white/50 text-sm py-6 text-center">Žádné rezervace ani blokace pro tento den.</p>}
          {items.map((it, i) => (
            <div key={i} className="border border-[#1f1f1f] rounded-sm p-4 bg-[#0d0d0d]" data-testid={`day-item-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[it.status] || "bg-white/40"}`} />
                    <span className="text-white font-medium truncate">{it.car_name}</span>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-white/10 text-white/60">
                      {it.type === "block" ? "Blokace" : "Rezervace"}
                    </span>
                  </div>
                  {it.type === "reservation" ? (
                    <div className="text-white/60 text-sm mt-1.5 space-y-0.5">
                      <p>📅 {fmtCz(it.pickup_date)} → {fmtCz(it.return_date)}</p>
                      <p>👤 {it.customer_name || "—"}{it.phone ? ` · ${it.phone}` : ""}</p>
                      {it.email ? <p className="truncate">✉️ {it.email}</p> : null}
                      <p>Stav: <span className="text-[#B58A50]">{STATUSES[it.status]?.label || it.status}</span></p>
                      {it.note ? <p className="text-white/50">📝 {it.note}</p> : null}
                    </div>
                  ) : (
                    <div className="text-white/60 text-sm mt-1.5">
                      <p>🚫 Vozidlo nedostupné{it.car?.available_from ? ` od ${fmtCz(it.car.available_from)}` : ""}{it.car?.available_to ? ` do ${fmtCz(it.car.available_to)}` : ""}.</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {it.type === "reservation" ? (
                  <>
                    <button onClick={() => onEditRes(it)} className="btn-outline-gold text-xs py-1.5 px-3" data-testid={`edit-res-${i}`}><Pencil size={13} /> Upravit</button>
                    <button onClick={() => onSplit(it)} className="btn-outline-gold text-xs py-1.5 px-3" data-testid={`split-res-${i}`}><Scissors size={13} /> Rozdělit</button>
                    <button onClick={() => onDeleteRes(it.id)} className="border border-red-500/40 text-red-400 rounded-sm text-xs py-1.5 px-3 hover:bg-red-500/10" data-testid={`delete-res-${i}`}><Trash2 size={13} /> Smazat</button>
                  </>
                ) : (
                  <button onClick={() => onEditBlock(it.car)} className="btn-outline-gold text-xs py-1.5 px-3" data-testid={`edit-block-${i}`}><Pencil size={13} /> Upravit blokaci</button>
                )}
              </div>
            </div>
          ))}

          <button onClick={onAdd} className="btn-gold w-full justify-center mt-2" data-testid="day-add-term"><Plus size={16} /> Přidat termín pro tento den</button>
        </div>
      </div>
    </div>
  );
}

function ResForm({ form, setForm, cars, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const isEdit = !!form.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    if (!form.car_id || !form.pickup_date || !form.return_date) return toast.error("Vyplňte vůz a termín.");
    setSaving(true);
    const car = cars.find((c) => c.id === form.car_id);
    const payload = {
      car_id: form.car_id,
      car_name: car?.name || form.car_name || "",
      pickup_date: form.pickup_date,
      return_date: form.return_date,
      customer_name: form.customer_name || "Blokace (admin)",
      phone: form.phone || "",
      email: form.email || "",
      note: form.note || "",
      status: form.status || "potvrzena",
    };
    try {
      if (isEdit) await api.put(`/admin/reservations/${form.id}`, payload);
      else await api.post("/admin/reservations", payload);
      toast.success(isEdit ? "Termín upraven" : "Termín přidán do kalendáře");
      onSaved();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4" data-testid="manual-res-modal">
      <div className="card-kame w-full max-w-md my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-white uppercase">{isEdit ? "Upravit termín" : "Přidat termín / blokaci"}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={22} /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div><label className={labelCls}>Vozidlo</label>
            <select value={form.car_id} onChange={(e) => set("car_id", e.target.value)} className={inputCls + " mt-1.5"} data-testid="manual-car">
              {cars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Od</label><input type="date" value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} className={inputCls + " mt-1.5"} data-testid="manual-from" /></div>
            <div><label className={labelCls}>Do</label><input type="date" value={form.return_date} onChange={(e) => set("return_date", e.target.value)} className={inputCls + " mt-1.5"} data-testid="manual-to" /></div>
          </div>
          <div><label className={labelCls}>Stav</label>
            <select value={form.status || "potvrzena"} onChange={(e) => set("status", e.target.value)} className={inputCls + " mt-1.5"} data-testid="manual-status">
              {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Zákazník / důvod</label><input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Blokace (admin)" className={inputCls + " mt-1.5"} data-testid="manual-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Telefon</label><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls + " mt-1.5"} data-testid="manual-phone" /></div>
            <div><label className={labelCls}>E-mail</label><input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls + " mt-1.5"} data-testid="manual-email" /></div>
          </div>
          <div><label className={labelCls}>Poznámka</label><textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} className={inputCls + " mt-1.5 resize-none"} data-testid="manual-note" /></div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-outline-gold flex-1 justify-center">Zrušit</button>
            <button type="submit" disabled={saving} className="btn-gold flex-1 justify-center disabled:opacity-60" data-testid="manual-save">{saving ? "Ukládám…" : "Uložit"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SplitForm({ res, onClose, onSaved }) {
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Zadejte datum rozdělení.");
    if (!(res.pickup_date < date && date < res.return_date)) return toast.error("Datum musí být uvnitř termínu.");
    setSaving(true);
    try {
      await api.post(`/admin/reservations/${res.id}/split`, { split_date: date });
      toast.success("Termín rozdělen na dvě části");
      onSaved();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="split-modal">
      <div className="card-kame w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-white uppercase">Rozdělit termín</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={22} /></button>
        </div>
        <p className="text-white/60 text-sm mb-4">{res.car_name}: {fmtCz(res.pickup_date)} → {fmtCz(res.return_date)}. Termín se rozdělí na dvě části v zadaném datu.</p>
        <form onSubmit={save} className="space-y-4">
          <div><label className={labelCls}>Datum rozdělení</label>
            <input type="date" value={date} min={res.pickup_date} max={res.return_date} onChange={(e) => setDate(e.target.value)} className={inputCls + " mt-1.5"} data-testid="split-date" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-outline-gold flex-1 justify-center">Zrušit</button>
            <button type="submit" disabled={saving} className="btn-gold flex-1 justify-center disabled:opacity-60" data-testid="split-save">{saving ? "Dělím…" : "Rozdělit"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BlockForm({ car, onClose, onSaved }) {
  const [from, setFrom] = useState(car.available_from || "");
  const [to, setTo] = useState(car.available_to || "");
  const [saving, setSaving] = useState(false);
  const submit = async (payload, msg) => {
    setSaving(true);
    try { await api.put(`/admin/cars/${car.id}/block`, payload); toast.success(msg); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="block-modal">
      <div className="card-kame w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-white uppercase">Blokace vozidla</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={22} /></button>
        </div>
        <p className="text-white/60 text-sm mb-4">{car.name}</p>
        <form onSubmit={(e) => { e.preventDefault(); submit({ available_from: from, available_to: to }, "Blokace uložena"); }} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Nedostupné od</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls + " mt-1.5"} data-testid="block-from" /></div>
            <div><label className={labelCls}>Nedostupné do</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls + " mt-1.5"} data-testid="block-to" /></div>
          </div>
          <p className="text-white/40 text-xs">Prázdná pole = vůz je dostupný. Jen „do" = obsazený do data. „od–do" = nedostupný v termínu. Jen „od" = nedostupný trvale.</p>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={() => submit({ available_from: "", available_to: "" }, "Blokace zrušena")} className="border border-red-500/40 text-red-400 rounded-sm px-3 py-2.5 text-sm hover:bg-red-500/10" data-testid="block-clear">Zrušit blokaci</button>
            <button type="submit" disabled={saving} className="btn-gold flex-1 justify-center disabled:opacity-60" data-testid="block-save">{saving ? "Ukládám…" : "Uložit"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- SETTINGS ---------------- */
function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const save = async () => {
    if (settings.order_mode === "resend" && !settings.resend_configured && !apiKey) {
      return toast.error("Zadejte prosím Resend API klíč.");
    }
    setSaving(true);
    try {
      const body = {
        order_mode: settings.order_mode,
        notify_email: settings.notify_email,
        resend_from: settings.resend_from,
      };
      if (apiKey) body.resend_api_key = apiKey;
      const { data } = await api.put("/admin/settings", body);
      setSettings(data);
      setApiKey("");
      toast.success("Nastavení uloženo");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <p className="text-white/50">Načítám…</p>;

  const opt = (id, Icon, title, desc) => {
    const active = settings.order_mode === id;
    return (
      <button
        type="button"
        onClick={() => setSettings({ ...settings, order_mode: id })}
        className={`card-kame p-5 text-left flex items-start gap-4 transition-colors ${active ? "border-[#B58A50] bg-[#B58A50]/10" : "hover:border-[#B58A50]/50"}`}
        data-testid={`order-mode-${id}`}
      >
        <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${active ? "border-[#B58A50]" : "border-white/30"}`}>
          {active && <span className="w-2.5 h-2.5 bg-[#B58A50] rounded-full" />}
        </span>
        <span>
          <Icon size={20} className="text-[#B58A50] mb-1" />
          <span className="block text-white font-medium">{title}</span>
          <span className="block text-white/50 text-sm">{desc}</span>
        </span>
      </button>
    );
  };

  return (
    <div data-testid="settings-tab" className="max-w-2xl space-y-6">
      <div>
        <h2 className="font-display text-2xl text-white uppercase">Nastavení objednávek</h2>
        <p className="text-white/50 text-sm">Zvolte, jak se budou doručovat nové rezervace.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {opt("admin", Inbox, "Do administrace", "Rezervace uvidíte v záložkách Rezervace a Kalendář.")}
        {opt("resend", Mail, "Přes Resend (API)", "Objednávky odesílá vaše Resend služba na zvolený e-mail.")}
      </div>

      {settings.order_mode === "resend" && (
        <div className="card-kame p-5 space-y-4" data-testid="resend-config">
          <div className="flex items-center gap-2 text-[#B58A50]">
            <KeyIcon size={18} />
            <span className="font-display uppercase text-sm tracking-wide">Napojení na Resend</span>
          </div>

          <div>
            <label className={labelCls}>Resend API klíč</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={settings.resend_configured ? `Uloženo: ${settings.resend_key_hint} — zadejte nový pro změnu` : "re_xxxxxxxxxxxxxxxx"}
              className={inputCls + " mt-1.5"}
              data-testid="resend-api-key"
            />
            <p className="text-white/40 text-xs mt-1.5">
              {settings.resend_configured
                ? "Klíč je uložen a spárován. Pole nechte prázdné, pokud jej nechcete měnit."
                : "Získáte na resend.com → API Keys. Klíč je uložen bezpečně na serveru."}
            </p>
          </div>

          <div>
            <label className={labelCls}>Odesílatel (from)</label>
            <input
              value={settings.resend_from || ""}
              onChange={(e) => setSettings({ ...settings, resend_from: e.target.value })}
              placeholder="KAME Autopůjčovna <rezervace@vasedomena.cz>"
              className={inputCls + " mt-1.5"}
              data-testid="resend-from"
            />
            <p className="text-white/40 text-xs mt-1.5">Musí být z domény ověřené ve vašem Resend účtu.</p>
          </div>

          <div>
            <label className={labelCls}>E-mail pro příjem objednávek</label>
            <input
              value={settings.notify_email}
              onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })}
              placeholder="vas@email.cz"
              className={inputCls + " mt-1.5"}
              data-testid="notify-email"
            />
          </div>

          {settings.resend_configured && (
            <p className="text-green-400/80 text-xs flex items-center gap-2" data-testid="resend-status">
              <span className="w-2 h-2 bg-green-400 rounded-full" /> Systém je spárován s Resend — objednávky odcházejí automaticky e-mailem.
            </p>
          )}
        </div>
      )}

      <button onClick={save} disabled={saving} className="btn-gold disabled:opacity-60" data-testid="save-settings">
        <Save size={18} /> {saving ? "Ukládám…" : "Uložit nastavení"}
      </button>

      <FacebookFeedCard />
      <DeliverySettingsCard />
    </div>
  );
}

function DeliverySettingsCard() {
  const [s, setS] = useState(null);
  const [orsKey, setOrsKey] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        delivery_enabled: !!s.delivery_enabled,
        delivery_price_per_km: Number(s.delivery_price_per_km) || 0,
        delivery_free_km: Number(s.delivery_free_km) || 0,
        delivery_round_trip: !!s.delivery_round_trip,
      };
      if (orsKey) body.ors_api_key = orsKey;
      const { data } = await api.put("/admin/settings", body);
      setS(data);
      setOrsKey("");
      toast.success("Nastavení přistavení uloženo");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  if (!s) return null;
  return (
    <div className="card-kame p-5 space-y-4" data-testid="delivery-settings-card">
      <div className="flex items-center gap-2 text-[#B58A50]">
        <MapPin size={18} />
        <span className="font-display uppercase text-sm tracking-wide">Přistavení vozu k zákazníkovi</span>
      </div>
      <p className="text-white/55 text-sm">
        Zákazník v rezervaci zadá adresu a město. Podle silniční vzdálenosti od provozovny (Hradecká 2152/35, Brno‑Řečkovice) se automaticky spočítají kilometry a cena přistavení.
      </p>

      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={!!s.delivery_enabled} onChange={(e) => set("delivery_enabled", e.target.checked)} className="accent-[#B58A50] w-4 h-4" data-testid="delivery-enabled" />
        <span className="text-white text-sm">Povolit výpočet přistavení na webu</span>
      </label>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Cena za km (Kč)</label>
          <input type="number" value={s.delivery_price_per_km} onChange={(e) => set("delivery_price_per_km", e.target.value)} className={inputCls + " mt-1.5"} data-testid="delivery-price-km" />
        </div>
        <div>
          <label className={labelCls}>Km zdarma</label>
          <input type="number" value={s.delivery_free_km} onChange={(e) => set("delivery_free_km", e.target.value)} className={inputCls + " mt-1.5"} data-testid="delivery-free-km" />
        </div>
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={!!s.delivery_round_trip} onChange={(e) => set("delivery_round_trip", e.target.checked)} className="accent-[#B58A50] w-4 h-4" data-testid="delivery-round-trip" />
        <span className="text-white text-sm">Počítat cestu tam i zpět (2× vzdálenost)</span>
      </label>

      <div>
        <label className={labelCls}>OpenRouteService API klíč</label>
        <input
          type="password"
          value={orsKey}
          onChange={(e) => setOrsKey(e.target.value)}
          placeholder={s.ors_configured ? `Uloženo: ${s.ors_key_hint} — zadejte nový pro změnu` : "Vložte API klíč z openrouteservice.org"}
          className={inputCls + " mt-1.5"}
          data-testid="ors-api-key"
        />
        <p className="text-white/40 text-xs mt-1.5">
          {s.ors_configured
            ? "Klíč je uložen bezpečně na serveru. Pole nechte prázdné, pokud jej nechcete měnit."
            : "Zdarma na openrouteservice.org → Sign up → Dashboard → Request a token. Klíč se uloží bezpečně na serveru."}
        </p>
      </div>

      <button onClick={save} disabled={saving} className="btn-gold disabled:opacity-60" data-testid="save-delivery-settings">
        <Save size={18} /> {saving ? "Ukládám…" : "Uložit nastavení přistavení"}
      </button>
    </div>
  );
}

function FacebookFeedCard() {
  const [copied, setCopied] = useState(false);
  const feedUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/api/facebook-feed`;
  const copy = () => {
    navigator.clipboard.writeText(feedUrl).then(() => {
      setCopied(true);
      toast.success("Odkaz zkopírován");
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="card-kame p-5 space-y-4" data-testid="facebook-feed-card">
      <div className="flex items-center gap-2 text-[#B58A50]">
        <Facebook size={18} />
        <span className="font-display uppercase text-sm tracking-wide">Facebook katalog (XML feed)</span>
      </div>
      <p className="text-white/55 text-sm">
        Automaticky generovaný katalog všech vozidel (CSV) pro Facebook Commerce Manager — připraveno pro katalog typu <b>Vozidla / půjčovna</b>, obsahuje i polohu provozovny a okruh dostupnosti. Facebook si sám stahuje aktuální nabídku — když změníte cenu nebo přidáte auto, katalog se aktualizuje.
      </p>
      <div>
        <label className={labelCls}>Odkaz na feed</label>
        <div className="flex gap-2 mt-1.5">
          <input readOnly value={feedUrl} className={inputCls + " text-[#B58A50]"} data-testid="facebook-feed-url" onFocus={(e) => e.target.select()} />
          <button type="button" onClick={copy} className="btn-outline-gold text-xs px-3 whitespace-nowrap" data-testid="copy-facebook-feed">
            {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Zkopírováno" : "Kopírovat"}
          </button>
        </div>
      </div>
      <ol className="text-white/60 text-xs space-y-1.5 list-decimal list-inside">
        <li>Otevřete Facebook <b>Commerce Manager</b> → Katalogy → Zdroje dat.</li>
        <li>Zvolte <b>„Naplánovaný přenos přes URL"</b> a vložte odkaz výše.</li>
        <li>Nastavte frekvenci aktualizace (např. každou hodinu) a dokončete.</li>
      </ol>
    </div>
  );
}


/* ---------------- EXPORT / SYSTEM ---------------- */
function ExportTab() {
  const [unlocked, setUnlocked] = useState(!!localStorage.getItem("kame_system_token"));
  const [password, setPassword] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");

  const loadInfo = () => {
    const token = localStorage.getItem("kame_system_token");
    if (!token) return;
    fetch(`${API}/system/info`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setInfo)
      .catch(() => { localStorage.removeItem("kame_system_token"); setUnlocked(false); });
  };
  useEffect(() => { if (unlocked) loadInfo(); }, [unlocked]);

  const unlock = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/system", { password });
      localStorage.setItem("kame_system_token", data.token);
      setUnlocked(true);
      toast.success("Systémová zóna odemčena");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const download = async (path, filename, key) => {
    setBusy(key);
    const token = localStorage.getItem("kame_system_token");
    try {
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Stahování zahájeno");
    } catch {
      toast.error("Stažení se nezdařilo");
    } finally {
      setBusy("");
    }
  };

  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto" data-testid="export-tab-locked">
        <div className="card-kame p-8 text-center">
          <span className="w-14 h-14 rounded-full bg-[#B58A50]/15 flex items-center justify-center text-[#B58A50] mx-auto mb-4"><Lock size={26} /></span>
          <h2 className="font-display text-xl text-white uppercase">Systémová zóna</h2>
          <p className="text-white/50 text-sm mt-1 mb-6">Zadejte heslo pro přístup k exportu projektu a databáze.</p>
          <form onSubmit={unlock} className="space-y-4">
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Systémové heslo" className={inputCls} data-testid="system-password" />
            <button type="submit" className="btn-gold w-full justify-center" data-testid="system-unlock">Odemknout</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="export-tab" className="space-y-6">
      <div>
        <h2 className="font-display text-2xl text-white uppercase">Systém & Export</h2>
        <p className="text-white/50 text-sm">Kompletní záloha pro migraci na jiný server.</p>
      </div>

      {info && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Vozidla" value={info.cars} />
          <Stat label="Rezervace" value={info.reservations} />
          <Stat label="Soubory" value={info.files} />
          <Stat label="Databáze" value={info.database} />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-kame p-6">
          <Package size={30} className="text-[#B58A50] mb-3" />
          <h3 className="font-display text-lg text-white uppercase">Celý projekt (ZIP)</h3>
          <p className="text-white/55 text-sm mt-2 mb-5">Frontend, backend, konfigurace i aktuální export databáze v jednom .zip souboru. Ideální pro nasazení na jiný server.</p>
          <button onClick={() => download("/system/export/project", "kame_projekt.zip", "zip")} disabled={busy === "zip"} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="download-project">
            <Download size={18} /> {busy === "zip" ? "Připravuji…" : "Stáhnout ZIP"}
          </button>
        </div>

        <div className="card-kame p-6">
          <Database size={30} className="text-[#B58A50] mb-3" />
          <h3 className="font-display text-lg text-white uppercase">Databáze (JSON)</h3>
          <p className="text-white/55 text-sm mt-2 mb-5">Aktuální export všech kolekcí databáze (vozidla, rezervace, uživatelé, soubory) ve formátu JSON.</p>
          <button onClick={() => download("/system/export/database", "kame_database.json", "db")} disabled={busy === "db"} className="btn-outline-gold w-full justify-center disabled:opacity-60" data-testid="download-database">
            <Download size={18} /> {busy === "db" ? "Připravuji…" : "Stáhnout databázi"}
          </button>
        </div>
      </div>

      <div className="card-kame p-6">
        <h3 className="font-display text-lg text-white uppercase mb-3">Návod na migraci</h3>
        <ol className="text-white/70 text-sm space-y-2 list-decimal list-inside">
          <li>Stáhněte ZIP celého projektu a rozbalte na cílovém serveru.</li>
          <li>Backend: <code className="text-[#B58A50]">pip install -r backend/requirements.txt</code>, nastavte <code className="text-[#B58A50]">backend/.env</code> (MONGO_URL, DB_NAME) a spusťte <code className="text-[#B58A50]">uvicorn server:app</code>.</li>
          <li>Frontend: <code className="text-[#B58A50]">yarn install</code>, nastavte <code className="text-[#B58A50]">REACT_APP_BACKEND_URL</code> a <code className="text-[#B58A50]">yarn build</code>.</li>
          <li>Databázi obnovte importem <code className="text-[#B58A50]">database_export.json</code> (součást ZIPu) do MongoDB.</li>
        </ol>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="card-kame p-5 text-center">
    <p className="font-display text-3xl text-[#B58A50]">{value}</p>
    <p className="text-white/50 text-xs uppercase tracking-wider mt-1">{label}</p>
  </div>
);
